// src/pages/VerifyEmailPage.js
import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { endpoints } from '../services/api';
import { Navigate } from 'react-router-dom';
import AuthShell from '../components/AuthShell';
import ReactGA from 'react-ga4';
import { postLoginDestination } from '../services/api';

const ERROR_MESSAGES = {
  invalid_token: "This verification link is invalid.",
  token_expired: "This link has expired. Please request a new one.",
};

const VerifyEmailPage = () => {
  const { token: authToken, login } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const token = searchParams.get('token');
  const email = searchParams.get('email');
  const error = searchParams.get('error');

  const [verifying, setVerifying] = useState(false);
  const [resent, setResent] = useState(false);

  // If already logged in and landed on an error page, just go home
  useEffect(() => {
    if (authToken && error) {
      navigate('/');
    }
  }, [authToken, error, navigate]);

  // ── Case 1: token in URL → magic link clicked → call backend to verify ──
  useEffect(() => {
    if (!token) return;

    let cancelled = false;

    const verify = async () => {
      setVerifying(true);
      try {
        const response = await fetch(
          `${endpoints.auth.verifyEmail}?token=${token}`
        );
        const data = await response.json();

        if (cancelled) return;

        if (!response.ok) {
          // Backend returned an error (invalid/expired token)
          navigate(`/auth/verify?error=${data.detail || 'invalid_token'}`);
          return;
        }

        // Backend returns JWT on success
        await login(data.access_token);
        // if (process.env.NODE_ENV === 'production') {
        ReactGA.event({ category: 'User', action: 'sign_up', label: 'email' });
        // }
        window.postMessage(
          { type: 'NEXAURA_AUTH_TOKEN', token: data.access_token },
          window.location.origin
        );
        // A brand-new user who arrived from a share link: resume it instead of
        // dumping them on the landing page with no idea what they were invited to.
        navigate(postLoginDestination(null));
      } catch {
        if (!cancelled) navigate('/auth/verify?error=invalid_token');
      } finally {
        if (!cancelled) setVerifying(false);
      }
    };

    verify();
    return () => { cancelled = true; };
  }, [token, login, navigate]);

  const handleResend = async () => {
    await fetch(`${endpoints.auth.resendVerification}?email=${encodeURIComponent(email)}`, {
      method: 'POST',
    });
    setResent(true);
  };

  // ── Case 2: token is being processed ────────────────────────────────────
  if (verifying || token) {
    return (
      <AuthShell display="One moment.">
          <h1 className="auth-title">Verifying your email</h1>
          <p className="auth-wait">Please wait while we verify your email.</p>
      </AuthShell>
    );
  }

  // ── Case 3: error state (invalid/expired token) ──────────────────────────
  if (error) {
    return (
      <AuthShell display="Link expired." say="Verification links last 15 minutes. I can send a new one.">
          <h1 className="auth-title">This link doesn’t work</h1>
          <p className="auth-error">
            {ERROR_MESSAGES[error] || "Something went wrong."}
          </p>
          {resent && <p className="auth-note">A new verification link is on its way.</p>}
          {email && (
            <button className="google-signin-button" onClick={handleResend}>
              Resend verification email
            </button>
          )}
          <p className="auth-switch">
            <button type="button" className="auth-linkbtn" onClick={() => navigate('/login')}>
              Back to login
            </button>
          </p>
      </AuthShell>
    );
  }

  // ── Case 4: after register → "check your email" screen ──────────────────
  if (!email) {
  return <Navigate to="/login" replace />;
}

return (
  <AuthShell display="One more click." say="Open the email I just sent and tap the link.">
      <h1 className="auth-title">Check your email</h1>
      <p className="auth-body">
        We sent a verification link to <strong>{email}</strong>.
      </p>
      <p className="auth-body">
        Click the link in the email to activate your account. It expires in 15 minutes.
      </p>
      {resent && <p className="auth-note">A new verification link is on its way.</p>}
      <button className="google-signin-button" onClick={handleResend}>
        Resend email
      </button>
  </AuthShell>
);
};

export default VerifyEmailPage;